import type { TemplateContent } from "@/types/template";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { PromptDialog } from "@/components/ui/PromptDialog";
import { TemplatePicker } from "@/components/ui/TemplatePicker";

interface ParsedTemplate {
  id: string;
  parsed: TemplateContent;
}

function toTemplateEntries(parsed: (ParsedTemplate & { id: string })[]) {
  return parsed.map((t) => ({
    category: t.parsed.category,
    id: t.id,
    name: t.parsed.name,
    sectionCount: t.parsed.content.filter((n) => n.type === "section").length,
  }));
}

interface AppDialogsProps {
  showEvalPicker: boolean;
  setShowEvalPicker: (v: boolean) => void;
  showClearConfirm: boolean;
  setShowClearConfirm: (v: boolean) => void;
  showPrompt: boolean;
  setShowPrompt: (v: boolean) => void;
  parsed: (ParsedTemplate & { id: string })[];
  onCreateEval: (id: string) => Promise<void>;
  onClearDb: () => Promise<void>;
  onCreateTemplate: (name: string) => Promise<void>;
}

function ClearAndPromptDialogs({
  onClearDb,
  onCreateTemplate,
  setShowClearConfirm,
  setShowPrompt,
  showClearConfirm,
  showPrompt,
}: Readonly<
  Pick<
    AppDialogsProps,
    | "showClearConfirm"
    | "setShowClearConfirm"
    | "showPrompt"
    | "setShowPrompt"
    | "onClearDb"
    | "onCreateTemplate"
  >
>) {
  return (
    <>
      <ConfirmDialog
        open={showClearConfirm}
        title="Clear All Data"
        message="This will reset to seed templates. All evaluations and custom templates will be permanently deleted."
        confirmLabel="Clear Everything"
        variant="danger"
        onConfirm={onClearDb}
        onCancel={() => {
          setShowClearConfirm(false);
        }}
      />
      <PromptDialog
        open={showPrompt}
        title="New Template"
        label="Template name"
        placeholder="e.g., Balance Assessment"
        onConfirm={onCreateTemplate}
        onCancel={() => {
          setShowPrompt(false);
        }}
      />
    </>
  );
}

export function AppDialogs(props: Readonly<AppDialogsProps>) {
  const templateEntries = toTemplateEntries(props.parsed);
  return (
    <>
      {props.showEvalPicker && (
        <TemplatePicker
          title="Choose a Template"
          templates={templateEntries}
          onSelect={props.onCreateEval}
          onClose={() => {
            props.setShowEvalPicker(false);
          }}
        />
      )}
      <ClearAndPromptDialogs
        showClearConfirm={props.showClearConfirm}
        setShowClearConfirm={props.setShowClearConfirm}
        showPrompt={props.showPrompt}
        setShowPrompt={props.setShowPrompt}
        onClearDb={props.onClearDb}
        onCreateTemplate={props.onCreateTemplate}
      />
    </>
  );
}
